import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Alert, Modal, Spinner } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
    Wifi, 
    Search, 
    Lock, 
    Server, 
    AlertTriangle, 
    RotateCcw, 
    ArrowLeft, 
    AlertCircle, 
    CheckCircle2, 
    ChevronRight, 
    Building2, 
    MapPin, 
    Edit3, 
    Trash2, 
    Briefcase, 
    IndianRupee, 
    Calendar, 
    Info, 
    FileText, 
    Users, 
    UserCircle2,
    Clock,
    Rocket,
    Heart,
    Eye,
    X
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { 
    getJobById, 
    checkJobStatus, 
    getJobCandidates, 
    applyForJob, 
    saveJob, 
    unsaveJob, 
    deleteJob 
} from '../services/jobService';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { Helmet } from 'react-helmet-async';
import './JobDetailsPage.css';
import { formatDate } from '../utils/dateUtils';
import { generateJobSlug } from '../utils/seo';

const JobDetailsPage = () => {
    const { id: rawId } = useParams();
    const id = rawId && rawId.includes('-') ? rawId.split('-').pop() : rawId;
    const navigate = useNavigate();
    const { user } = useAuth();
    
    const [job, setJob] = useState(null);
    const [candidates, setCandidates] = useState([]);
    const [jobStatus, setJobStatus] = useState({ alreadyApplied: false, alreadySaved: false });
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState(null);
    const [errorType, setErrorType] = useState(null); 
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [notification, setNotification] = useState(null);
    const [selectedCandidates, setSelectedCandidates] = useState([]);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [showCandidateModal, setShowCandidateModal] = useState(false);

    // Resume preview state variables
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewType, setPreviewType] = useState(''); // 'pdf' | 'image' | 'word' | 'other'
    const [previewLoading, setPreviewLoading] = useState(false);

    const handleDownloadResume = async (applicationId, candidateName) => {
        try {
            const token = localStorage.getItem(process.env.REACT_APP_JWT_STORAGE_KEY || 'jwt-token');
            const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';
            const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
            
            const response = await fetch(`${cleanBaseUrl}/resume/download/${applicationId}`, {
                headers: { 
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                let errorMsg = 'Failed to download resume';
                try {
                    const errData = await response.json();
                    errorMsg = errData.error || errorMsg;
                } catch (e) {}
                throw new Error(errorMsg);
            }

            // Extract correct file extension from headers
            let extension = 'pdf';
            const contentDisposition = response.headers.get('content-disposition');
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch && filenameMatch[1]) {
                    const filename = filenameMatch[1];
                    const lastDot = filename.lastIndexOf('.');
                    if (lastDot !== -1) {
                        extension = filename.substring(lastDot + 1);
                    }
                }
            } else {
                const contentType = response.headers.get('content-type');
                if (contentType) {
                    const ct = contentType.toLowerCase();
                    if (ct.includes('word') || ct.includes('msword') || ct.includes('officedocument.wordprocessingml.document')) {
                        extension = 'docx';
                    } else if (ct.includes('image/png')) {
                        extension = 'png';
                    } else if (ct.includes('image/jpeg') || ct.includes('image/jpg')) {
                        extension = 'jpg';
                    } else if (ct.includes('image/gif')) {
                        extension = 'gif';
                    }
                }
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${candidateName.replace(/\s+/g, '_')}_resume.${extension}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            showNotification(`Downloaded resume for ${candidateName}!`, 'success');
        } catch (err) {
            console.error('Download error:', err);
            showNotification(err.message || 'Error downloading resume', 'error');
        }
    };

    // Clean up preview object URLs
    useEffect(() => {
        return () => {
            if (previewUrl && typeof previewUrl === 'string') {
                window.URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const handleCloseCandidateModal = () => {
        if (previewUrl && typeof previewUrl === 'string') {
            window.URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl(null);
        setPreviewType('');
        setPreviewLoading(false);
        setShowCandidateModal(false);
    };

    const handleViewResume = async (applicationId, resumePath) => {
        if (!resumePath) return;

        const isWord = resumePath.toLowerCase().endsWith('.docx') || resumePath.toLowerCase().endsWith('.doc');
        if (isWord) {
            setPreviewType('word');
            setPreviewUrl('word-placeholder'); // Truthy value to show warning panel
            return;
        }

        try {
            setPreviewLoading(true);
            setPreviewUrl(null);

            const token = localStorage.getItem(process.env.REACT_APP_JWT_STORAGE_KEY || 'jwt-token');
            const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';
            const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
            
            const response = await fetch(`${cleanBaseUrl}/resume/download/${applicationId}`, {
                headers: { 
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                let errorMsg = 'Failed to fetch resume file';
                try {
                    const errData = await response.json();
                    errorMsg = errData.error || errorMsg;
                } catch (e) {}
                throw new Error(errorMsg);
            }

            const blob = await response.blob();
            const isImage = resumePath.toLowerCase().endsWith('.png') || 
                            resumePath.toLowerCase().endsWith('.jpg') || 
                            resumePath.toLowerCase().endsWith('.jpeg') ||
                            blob.type.startsWith('image/');
                            
            const isPdf = resumePath.toLowerCase().endsWith('.pdf') || 
                          blob.type === 'application/pdf';

            if (isImage) {
                setPreviewType('image');
            } else if (isPdf) {
                setPreviewType('pdf');
            } else {
                setPreviewType('other');
            }

            const url = window.URL.createObjectURL(blob);
            setPreviewUrl(url);
        } catch (err) {
            console.error('Error viewing resume:', err);
            showNotification(err.message || 'Error loading resume preview', 'error');
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleToggleSelect = (appId) => {
        if (!appId) return;
        setSelectedCandidates(prev => 
            prev.includes(appId) 
                ? prev.filter(id => id !== appId) 
                : [...prev, appId]
        );
    };

    const handleSelectAll = () => {
        const allIds = candidates
            .map(c => c.applicationId || c.id)
            .filter(Boolean);
            
        if (selectedCandidates.length === allIds.length) {
            setSelectedCandidates([]);
        } else {
            setSelectedCandidates(allIds);
        }
    };

    const handleBulkDownload = async () => {
        if (selectedCandidates.length === 0) return;
        
        try {
            setDownloadLoading(true);
            const token = localStorage.getItem(process.env.REACT_APP_JWT_STORAGE_KEY || 'jwt-token');
            const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';
            const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
            
            const response = await fetch(`${cleanBaseUrl}/resume/download/bulk?applicationIds=${selectedCandidates.join(',')}`, {
                headers: { 
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                let errorMsg = 'Failed to download resumes';
                try {
                    const errData = await response.json();
                    errorMsg = errData.error || errorMsg;
                } catch (e) {}
                throw new Error(errorMsg);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `resumes_job_${id}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            showNotification('Successfully downloaded resumes ZIP!', 'success');
        } catch (err) {
            console.error('Bulk download error:', err);
            showNotification(err.message || 'Error generating bulk resumes download', 'error');
        } finally {
            setDownloadLoading(false);
        }
    };

    const loadJobDetails = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            setErrorType(null);

            // 1. Load basic job details
            const jobData = await getJobById(id);
            setJob(jobData);

            // Sequential Fetching for user-specific data to avoid 503
            await new Promise(r => setTimeout(r, 100));

            // 2. Load job status for job seekers
            if (user?.userType === 'Job Seeker') {
                try {
                    const statusData = await checkJobStatus(id);
                    setJobStatus(statusData || { alreadyApplied: false, alreadySaved: false });
                } catch (statusErr) {
                    console.warn('Failed to load job status:', statusErr);
                }
            }

            // 3. Load candidates for recruiters
            if (user?.userType === 'Recruiter') {
                try {
                    const candidatesResponse = await getJobCandidates(id);
                    const candidatesList = candidatesResponse?.data || (Array.isArray(candidatesResponse) ? candidatesResponse : []);
                    setCandidates(candidatesList);
                } catch (candidatesErr) {
                    console.warn('Failed to load candidates:', candidatesErr);
                    setCandidates([]);
                }
            }

        } catch (err) {
            const errorInfo = handleApiError(err, 'job');
            setError(errorInfo.message);
            setErrorType(errorInfo.type);
        } finally {
            setLoading(false);
        }
    }, [id, user?.userType]);

    useEffect(() => {
        loadJobDetails();
    }, [loadJobDetails]);

    // Helper function for API errors
    const handleApiError = (err, context) => {
        let errorMessage = 'An unexpected error occurred';
        let errorTypeCode = 'GENERAL_ERROR';

        if (err.response) {
            const status = err.response.status;
            switch (status) {
                case 400:
                    errorMessage = `Invalid request: ${err.response.data?.message || 'Bad request'}`;
                    errorTypeCode = 'INVALID_REQUEST';
                    break;
                case 401:
                    errorMessage = 'You are not authorized to perform this action. Please log in again.';
                    errorTypeCode = 'UNAUTHORIZED';
                    break;
                case 403:
                    errorMessage = 'You do not have permission to access this resource.';
                    errorTypeCode = 'FORBIDDEN';
                    break;
                case 404:
                    errorMessage = context === 'job' ? 'Job not found. It may have been deleted.' : 'Resource not found';
                    errorTypeCode = 'NOT_FOUND';
                    break;
                case 409:
                    errorMessage = 'You have already applied for this job.';
                    errorTypeCode = 'CONFLICT';
                    // Soft success: update state since it's already in the DB
                    if (context === 'apply') {
                        setJobStatus(prev => ({ ...prev, alreadyApplied: true }));
                    }
                    break;
                case 500:
                    errorMessage = 'Server error occurred. Please try again later.';
                    errorTypeCode = 'SERVER_ERROR';
                    break;
                default:
                    errorMessage = `Request failed with status ${status}`;
                    errorTypeCode = 'HTTP_ERROR';
            }
        } else if (err.request) {
            errorMessage = 'Network error. Please check your internet connection.';
            errorTypeCode = 'NETWORK_ERROR';
        } else {
            errorMessage = err.message || errorMessage;
            errorTypeCode = 'GENERAL_ERROR';
        }

        return { message: errorMessage, type: errorTypeCode };
    };

    const handleApplyJob = async () => {
        try {
            setActionLoading(true);
            const jobResponse = await applyForJob(id);
            
            // Ensure we handle various response structures
            const isSuccess = jobResponse && (jobResponse.success === true || jobResponse.jobPostId);
            
            if (!isSuccess) {
                const errMsg = jobResponse?.message || 'Failed to apply for job. Please try again.';
                showNotification(errMsg, 'error');
                return;
            }
            setJobStatus(prev => ({ ...prev, alreadyApplied: true }));
            showNotification('Successfully applied for the job!', 'success');
        } catch (err) {
            const errorInfo = handleApiError(err, 'apply');
            showNotification(errorInfo.message, 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleSaveJob = async () => {
        try {
            setActionLoading(true);
            if (jobStatus.alreadySaved) {
                await unsaveJob(id);
                setJobStatus(prev => ({ ...prev, alreadySaved: false }));
                showNotification('Job removed from saved jobs', 'success');
            } else {
                await saveJob(id);
                setJobStatus(prev => ({ ...prev, alreadySaved: true }));
                showNotification('Job saved successfully!', 'success');
            }
        } catch (err) {
            const errorInfo = handleApiError(err, 'save');
            showNotification(errorInfo.message, 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleEditJob = () => {
        navigate(`/dashboard/edit/${id}`);
    };

    const handleDeleteJob = async () => {
        try {
            setActionLoading(true);
            await deleteJob(id);
            showNotification('Job deleted successfully!', 'success');
            setTimeout(() => {
                navigate('/dashboard');
            }, 2000);
        } catch (err) {
            const errorInfo = handleApiError(err, 'delete');
            showNotification(errorInfo.message, 'error');
        } finally {
            setActionLoading(false);
            setShowDeleteModal(false);
        }
    };

    const formatSalary = (salary) => {
        if (!salary) return '';
        // Fix mangled Rupee symbol encoding and normalize dollar signs
        return salary.replace(/â‚¹/g, '₹').replace(/\$/g, '₹');
    };

    const showNotification = (message, type) => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 5000);
    };



    const goBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/jobs');
        }
    };

    // Enhanced retry function
    const handleRetry = () => {
        if (errorType === 'UNAUTHORIZED') {
            // Redirect to login for auth errors
            navigate('/login');
        } else {
            // Retry loading job details
            loadJobDetails();
        }
    };

    // Enhanced error display component
    const renderError = () => {
        const getErrorIcon = () => {
            switch (errorType) {
                case 'NETWORK_ERROR':
                    return Wifi;
                case 'NOT_FOUND':
                    return Search;
                case 'UNAUTHORIZED':
                case 'FORBIDDEN':
                    return Lock;
                case 'SERVER_ERROR':
                    return Server;
                default:
                    return AlertTriangle;
            }
        };

        const Icon = getErrorIcon();

        const getRetryButtonText = () => {
            switch (errorType) {
                case 'UNAUTHORIZED':
                    return 'Go to Login';
                case 'NETWORK_ERROR':
                    return 'Check Connection & Retry';
                case 'NOT_FOUND':
                    return 'Search for Jobs';
                default:
                    return 'Try Again';
            }
        };

        const getSecondaryAction = () => {
            if (errorType === 'NOT_FOUND') {
                return (
                    <Button 
                        variant="outline-primary" 
                        onClick={() => navigate('/jobs')}
                        className="ms-2 d-flex align-items-center"
                    >
                        <Search size={18} className="me-2" />
                        Browse All Jobs
                    </Button>
                );
            }
            return null;
        };

        return (
            <Container className="py-5">
                <Row>
                    <Col>
                        <Alert variant="danger" className="text-center error-alert">
                            <div className="error-content">
                                <Icon size={48} className="mb-3 text-danger" />
                                <h4 className="mb-3">Oops! Something went wrong</h4>
                                <p className="mb-4">{error}</p>
                                
                                <div className="error-actions d-flex justify-content-center">
                                    <Button 
                                        variant="primary" 
                                        onClick={handleRetry}
                                        disabled={loading}
                                        className="d-flex align-items-center"
                                    >
                                        {loading ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Loading...
                                            </>
                                        ) : (
                                            <>
                                                <RotateCcw size={18} className="me-2" />
                                                {getRetryButtonText()}
                                            </>
                                        )}
                                    </Button>
                                    
                                    {getSecondaryAction()}
                                    
                                    <Button 
                                        variant="outline-secondary" 
                                        onClick={goBack}
                                        className="ms-2 d-flex align-items-center"
                                    >
                                        <ArrowLeft size={18} className="me-2" />
                                        Go Back
                                    </Button>
                                </div>

                                {errorType === 'NETWORK_ERROR' && (
                                    <div className="mt-3">
                                        <small className="text-muted d-flex align-items-center justify-content-center">
                                            <Info size={14} className="me-1" />
                                            Please check your internet connection and try again
                                        </small>
                                    </div>
                                )}
                            </div>
                        </Alert>
                    </Col>
                </Row>
            </Container>
        );
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return renderError();
    }

    if (!job) {
        return (
            <Container className="py-5">
                <Row>
                    <Col>
                        <Alert variant="warning" className="text-center">
                            <AlertCircle size={24} className="me-2" />
                            Job not found
                        </Alert>
                    </Col>
                </Row>
            </Container>
        );
    }

    const locationCity = job.jobLocationId?.city || job.location || 'Unknown Location';
    const locationCountry = job.jobLocationId?.country || 'IN';
    const locationRegion = job.jobLocationId?.state || '';
    const salary = job.salary || '';
    const isRemote = job.remote === 'Remote-Only' || job.remote === 'Partial-Remote';
    const companyName = job.jobCompanyId?.name || job.companyName || 'Zpluse Jobs';

    // Helper to get Google standard employment type
    const getGoogleEmploymentType = (jobType) => {
        if (!jobType) return 'FULL_TIME';
        const type = jobType.toLowerCase().trim();
        if (type.includes('full')) return 'FULL_TIME';
        if (type.includes('part')) return 'PART_TIME';
        if (type.includes('contract') || type.includes('freelance')) return 'CONTRACTOR';
        if (type.includes('intern')) return 'INTERN';
        if (type.includes('temp') || type.includes('temporary')) return 'TEMPORARY';
        if (type.includes('volunteer')) return 'VOLUNTEER';
        return 'OTHER';
    };

    // Helper to parse salary range/value into numerical format for Google schema
    const parseSalary = (salaryStr) => {
        if (!salaryStr) return null;
        // Strip out currency symbols and commas, keep digits, dots, and hyphens
        const cleanStr = salaryStr.replace(/[^0-9.-]/g, '');
        const parts = cleanStr.split('-').map(p => parseFloat(p.trim())).filter(p => !isNaN(p));
        if (parts.length === 1) {
            return {
                '@type': 'QuantitativeValue',
                value: parts[0],
                unitText: 'YEAR'
            };
        } else if (parts.length >= 2) {
            return {
                '@type': 'QuantitativeValue',
                minValue: parts[0],
                maxValue: parts[1],
                unitText: 'YEAR'
            };
        }
        return null;
    };

    // Calculate validThrough as 30 days after datePosted
    const datePostedStr = job.postedDate
        ? new Date(job.postedDate).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0];
    
    const validThroughDate = new Date(datePostedStr);
    validThroughDate.setDate(validThroughDate.getDate() + 30);
    const validThroughStr = validThroughDate.toISOString().split('T')[0] + 'T00:00:00Z';

    const plainTextDescription = (job.descriptionOfJob || '').replace(/<[^>]+>/g, '').substring(0, 155);

    const parsedSalaryValue = parseSalary(salary);

    const jsonLd = {
        '@context': 'https://schema.org/',
        '@type': 'JobPosting',
        title: job.jobTitle || '',
        description: job.descriptionOfJob || '',
        identifier: {
            '@type': 'PropertyValue',
            name: companyName,
            value: String(job.jobPostId || ''),
        },
        datePosted: datePostedStr + 'T00:00:00Z',
        validThrough: validThroughStr,
        employmentType: getGoogleEmploymentType(job.jobType),
        hiringOrganization: {
            '@type': 'Organization',
            name: companyName,
            sameAs: 'https://www.zplusejobs.com',
        },
        jobLocation: {
            '@type': 'Place',
            address: {
                '@type': 'PostalAddress',
                addressLocality: locationCity,
                addressRegion: locationRegion,
                addressCountry: locationCountry,
            },
        },
        ...(isRemote ? { jobLocationType: 'TELECOMMUTE' } : {}),
        ...(parsedSalaryValue ? {
            baseSalary: {
                '@type': 'MonetaryAmount',
                currency: 'INR',
                value: parsedSalaryValue,
            },
        } : {}),
        url: `https://www.zplusejobs.com/jobs/${generateJobSlug(job)}`,
        directApply: true,
    };

    return (
        <div className="job-details-page">
            <Helmet>
                <title>{job.jobTitle} in {locationCity} | {companyName} Careers | Zpluse Jobs</title>
                <meta name="description" content={plainTextDescription} />
                <meta property="og:title" content={`${job.jobTitle} at ${companyName}`} />
                <meta property="og:description" content={plainTextDescription} />
                <meta property="og:url" content={`https://www.zplusejobs.com/jobs/${generateJobSlug(job)}`} />
                <meta property="og:type" content="website" />
                <script type="application/ld+json">
                    {JSON.stringify(jsonLd)}
                </script>
            </Helmet>
            <Container className="py-4">
                {/* Notification */}
                {notification && (
                    <Alert 
                        variant={notification.type === 'success' ? 'success' : 'danger'} 
                        className="mb-4 modern-alert"
                        dismissible 
                        onClose={() => setNotification(null)}
                    >
                        <div className="d-flex align-items-center">
                            {notification.type === 'success' ? <CheckCircle2 size={18} className="me-3" /> : <AlertCircle size={18} className="me-3" />}
                            <span>{notification.message}</span>
                        </div>
                    </Alert>
                )}

                {/* Header Section */}
                <div className="job-details-header mb-5">
                    <div className="d-flex align-items-center mb-4">
                        <Button 
                            variant="outline-primary" 
                            size="sm"
                            className="back-btn me-3 d-flex align-items-center" 
                            onClick={goBack}
                        >
                            <ArrowLeft size={16} className="me-2" />
                            Back
                        </Button>
                        
                        <div className="page-breadcrumb text-muted">
                            <small className="d-flex align-items-center">
                                <Link to="/dashboard" className="text-decoration-none">Dashboard</Link>
                                <ChevronRight size={14} className="mx-2" />
                                <span>Job Details</span>
                            </small>
                        </div>
                    </div>
                    
                    <div className="job-header-card">
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="p-4">
                                <div className="job-header-content">
                                    <div className="d-flex align-items-start mb-4">
                                        <div className="company-logo me-4">
                                            <div className="logo-placeholder">
                                                <Building2 size={32} />
                                            </div>
                                        </div>
                                        <div className="flex-grow-1">
                                            <h1 className="job-title mb-3">{job.jobTitle}</h1>
                                            <div className="company-info mb-3">
                                                <h5 className="company-name mb-2">
                                                    <Link 
                                                        to={`/companies/${job.postedBy?.userId || job.postedById?.userId}`} 
                                                        className="text-primary text-decoration-none hover-underline"
                                                    >
                                                        {job.jobCompanyId?.name || job.companyName || 'Company not specified'}
                                                    </Link>
                                                </h5>
                                                <div className="location-info text-muted d-flex align-items-center">
                                                    <MapPin size={16} className="me-2" />
                                                    {job.jobLocationId ? 
                                                        `${job.jobLocationId.city}, ${job.jobLocationId.state}` : 
                                                        'Location not specified'
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Recruiter Actions - Right Aligned */}
                                        {user?.userType === 'Recruiter' && (
                                            <div className="recruiter-actions ms-3">
                                                <Button
                                                    variant="primary"
                                                    size="lg"
                                                    className="me-2 mb-2 d-flex align-items-center"
                                                    onClick={handleEditJob}
                                                    disabled={actionLoading}
                                                >
                                                    <Edit3 size={18} className="me-2" />
                                                    Edit Job
                                                </Button>
                                                
                                                <Button
                                                    variant="outline-danger"
                                                    size="lg"
                                                    className="mb-2 d-flex align-items-center"
                                                    onClick={() => setShowDeleteModal(true)}
                                                    disabled={actionLoading}
                                                >
                                                    <Trash2 size={18} className="me-2" />
                                                    Delete
                                                </Button>
                                            </div>
                                        )}

                                        {/* Job Seeker Actions - Right Aligned */}
                                        {user?.userType === 'Job Seeker' && (
                                            <div className="job-seeker-actions ms-3">
                                                <Button
                                                    variant={jobStatus.alreadyApplied ? "success" : "primary"}
                                                    size="lg"
                                                    className="me-2 mb-2 d-flex align-items-center"
                                                    onClick={handleApplyJob}
                                                    disabled={actionLoading || jobStatus.alreadyApplied}
                                                >
                                                    {jobStatus.alreadyApplied ? (
                                                        <><CheckCircle2 size={18} className="me-2" /> Applied</>
                                                    ) : (
                                                        <><Rocket size={18} className="me-2" /> Apply Now</>
                                                    )}
                                                </Button>
                                                
                                                <Button
                                                    variant={jobStatus.alreadySaved ? "warning" : "outline-primary"}
                                                    size="lg"
                                                    className="mb-2 d-flex align-items-center"
                                                    onClick={handleSaveJob}
                                                    disabled={actionLoading}
                                                >
                                                    {jobStatus.alreadySaved ? (
                                                        <><Heart size={18} className="me-2 fill-current" /> Saved</>
                                                    ) : (
                                                        <><Heart size={18} className="me-2" /> Save Job</>
                                                    )}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Job Tags */}
                                    <div className="job-tags">
                                        {job.jobType && (
                                            <span className="badge bg-primary me-2 mb-2 d-inline-flex align-items-center">
                                                <Briefcase size={14} className="me-1" />
                                                {job.jobType}
                                            </span>
                                        )}
                                        {job.remote && (
                                            <span className="badge bg-success me-2 mb-2 d-inline-flex align-items-center">
                                                <Wifi size={14} className="me-1" />
                                                {job.remote}
                                            </span>
                                        )}
                                        {job.salary && (
                                            <span className="badge bg-warning text-dark me-2 mb-2 d-inline-flex align-items-center">
                                                <IndianRupee size={14} className="me-1" />
                                                {formatSalary(job.salary)}
                                            </span>
                                        )}
                                        <span className="badge bg-secondary me-2 mb-2 d-inline-flex align-items-center">
                                            <Calendar size={14} className="me-1" />
                                            Posted {formatDate(job.postedDate)}
                                        </span>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                </div>

                {/* Main Content */}
                <Row>
                    {/* Left Column - Job Details */}
                    <Col lg={user?.userType === 'Recruiter' ? 8 : 12}>
                        {/* Job Specifications */}
                        <Card className="job-specs-card mb-4 border-0 shadow-sm">
                            <Card.Body className="p-4">
                                <h2 className="section-title mb-4 d-flex align-items-center">
                                    <Info size={24} className="me-2 text-primary" />
                                    Job Information
                                </h2>
                                
                                <Row className="job-specs-grid">
                                    <Col md={6} lg={3} className="mb-3">
                                        <div className="spec-card">
                                            <div className="spec-icon">
                                                <Calendar size={20} />
                                            </div>
                                            <div className="spec-content">
                                                <label className="spec-label">Date Posted</label>
                                                <span className="spec-value">{formatDate(job.postedDate)}</span>
                                            </div>
                                        </div>
                                    </Col>
                                    
                                    <Col md={6} lg={3} className="mb-3">
                                        <div className="spec-card">
                                            <div className="spec-icon">
                                                <IndianRupee size={20} />
                                            </div>
                                            <div className="spec-content">
                                                <label className="spec-label">Salary</label>
                                                <span className="spec-value">{formatSalary(job.salary) || 'Not specified'}</span>
                                            </div>
                                        </div>
                                    </Col>
                                    
                                    <Col md={6} lg={3} className="mb-3">
                                        <div className="spec-card">
                                            <div className="spec-icon">
                                                <Briefcase size={20} />
                                            </div>
                                            <div className="spec-content">
                                                <label className="spec-label">Job Type</label>
                                                <span className="spec-value">{job.jobType || 'Not specified'}</span>
                                            </div>
                                        </div>
                                    </Col>
                                    
                                    <Col md={6} lg={3} className="mb-3">
                                        <div className="spec-card">
                                            <div className="spec-icon">
                                                <Wifi size={20} />
                                            </div>
                                            <div className="spec-content">
                                                <label className="spec-label">Work Type</label>
                                                <span className="spec-value">{job.remote || 'Not specified'}</span>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Job Description */}
                        <Card className="job-description-card border-0 shadow-sm">
                            <Card.Body className="p-4">
                                <h2 className="section-title mb-4 d-flex align-items-center">
                                    <FileText size={24} className="me-2 text-primary" />
                                    Job Description
                                </h2>
                                <div 
                                    className="job-description-content"
                                    dangerouslySetInnerHTML={{ __html: job.descriptionOfJob || '<p class="text-muted">No description provided.</p>' }}
                                />
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Right Column - Candidates Sidebar (Recruiter Only) */}
                    {user?.userType === 'Recruiter' && (
                        <Col lg={4}>
                            <Card className="candidates-sidebar-card border-0 shadow-sm">
                                <Card.Body className="p-4">
                                    <div className="candidates-header mb-4">
                                        <h5 className="section-title mb-0 d-flex align-items-center">
                                            <Users size={20} className="me-2 text-primary" />
                                            Applications
                                            <span className="badge bg-primary ms-2">{candidates.length}</span>
                                        </h5>
                                    </div>

                                    {candidates.length > 0 && (
                                        <div className="bulk-download-controls mb-3 p-3 bg-light rounded d-flex flex-column gap-2 border">
                                            <div className="d-flex align-items-center justify-content-between">
                                                <div className="form-check d-flex align-items-center m-0">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input cursor-pointer"
                                                        id="select-all-candidates"
                                                        checked={selectedCandidates.length === candidates.length && candidates.length > 0}
                                                        onChange={handleSelectAll}
                                                        style={{ width: '1.2rem', height: '1.2rem', cursor: 'pointer' }}
                                                    />
                                                    <label 
                                                        className="form-check-label ms-2 text-muted fw-semibold small cursor-pointer" 
                                                        htmlFor="select-all-candidates"
                                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                                    >
                                                        Select All ({selectedCandidates.length}/{candidates.length})
                                                    </label>
                                                </div>
                                            </div>
                                            
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={handleBulkDownload}
                                                disabled={selectedCandidates.length === 0 || downloadLoading}
                                                className="w-100 d-flex align-items-center justify-content-center py-2"
                                            >
                                                {downloadLoading ? (
                                                    <>
                                                        <Spinner size="sm" className="me-2" />
                                                        Downloading...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FileText size={16} className="me-2" />
                                                        Download {selectedCandidates.length} Resume{selectedCandidates.length > 1 ? 's' : ''}
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    )}
                                    
                                    {candidates.length > 0 ? (
                                        <div className="candidates-list">
                                            {candidates.map((candidate, index) => {
                                                const candidateId = candidate.applicationId || candidate.id;
                                                const profileId = candidate.userId?.userAccountId || candidate.userId;
                                                const candidateName = candidate.firstName && candidate.lastName 
                                                    ? `${candidate.firstName} ${candidate.lastName}`
                                                    : (candidate.userId && typeof candidate.userId === 'object'
                                                        ? `${candidate.userId.firstName} ${candidate.userId.lastName}`
                                                        : 'Anonymous User');
                                                
                                                return (
                                                    <div key={index} className="candidate-card-wrapper d-flex align-items-center mb-3">
                                                        <div className="form-check me-2 m-0">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input cursor-pointer"
                                                                checked={selectedCandidates.includes(candidateId)}
                                                                onChange={() => handleToggleSelect(candidateId)}
                                                                style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
                                                            />
                                                        </div>
                                                        <div className="flex-grow-1">
                                                            <div 
                                                                onClick={() => {
                                                                    setSelectedCandidate(candidate);
                                                                    setShowCandidateModal(true);
                                                                }}
                                                                className="candidate-link text-decoration-none"
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                <div className="candidate-card p-2 rounded border hover-shadow d-flex align-items-center" style={{ transition: 'all 0.2s' }}>
                                                                    <div className="candidate-avatar me-3">
                                                                        <UserCircle2 size={32} className="text-primary" />
                                                                    </div>
                                                                    <div className="candidate-info">
                                                                        <h6 className="candidate-name mb-1 text-dark">
                                                                            {candidateName}
                                                                        </h6>
                                                                        <small className="text-muted d-flex align-items-center">
                                                                            <Clock size={12} className="me-1" />
                                                                            Applied {candidate.appliedDate ? formatDate(candidate.appliedDate) : 'recently'}
                                                                        </small>
                                                                    </div>
                                                                    <div className="ms-auto">
                                                                        <ChevronRight size={16} className="text-muted" />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="no-candidates text-center py-4">
                                            <Users size={48} className="text-muted mb-3" />
                                            <h6 className="text-muted mb-2">No Applications Yet</h6>
                                            <p className="text-muted small mb-0">
                                                When candidates apply for this job, they'll appear here.
                                            </p>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    )}
                </Row>
            </Container>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="d-flex align-items-center">
                        <AlertTriangle size={24} className="text-danger me-2" />
                        Confirm Delete
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete this job posting?</p>
                    <p><strong>"{job?.jobTitle}"</strong></p>
                    <p className="text-muted">This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={handleDeleteJob}
                        disabled={actionLoading}
                        className="d-flex align-items-center"
                    >
                        {actionLoading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Deleting...
                            </>
                        ) : (
                            <>
                                <Trash2 size={18} className="me-2" />
                                Delete Job
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Candidate Details Modal */}
            <Modal show={showCandidateModal} onHide={handleCloseCandidateModal} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title className="d-flex align-items-center fw-bold">
                        <Users size={24} className="text-primary me-2" />
                        Candidate Application Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {selectedCandidate && (
                        <div>
                            <h4 className="fw-bold mb-3">
                                {selectedCandidate.firstName && selectedCandidate.lastName 
                                    ? `${selectedCandidate.firstName} ${selectedCandidate.lastName}`
                                    : (selectedCandidate.userId && typeof selectedCandidate.userId === 'object'
                                        ? `${selectedCandidate.userId.firstName} ${selectedCandidate.userId.lastName}`
                                        : 'Anonymous User')}
                            </h4>
                            <p className="text-muted mb-4 d-flex align-items-center">
                                <Clock size={16} className="me-2" />
                                Applied: {selectedCandidate.appliedDate ? formatDate(selectedCandidate.appliedDate) : 'recently'}
                            </p>
                            
                            <Row className="g-4 mb-4">
                                <Col md={6}>
                                    <div className="p-3 bg-light rounded-3 h-100 border">
                                        <div className="small text-muted text-uppercase fw-bold mb-1">Email Address</div>
                                        <div className="fw-semibold text-dark">{selectedCandidate.email || (selectedCandidate.userId && typeof selectedCandidate.userId === 'object' ? selectedCandidate.userId.email : 'N/A')}</div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="p-3 bg-light rounded-3 h-100 border">
                                        <div className="small text-muted text-uppercase fw-bold mb-1">Employment Preference</div>
                                        <div className="fw-semibold text-dark">{selectedCandidate.employmentType || 'Not Specified'}</div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="p-3 bg-light rounded-3 h-100 border">
                                        <div className="small text-muted text-uppercase fw-bold mb-1">Work Authorization</div>
                                        <div className="fw-semibold text-dark">{selectedCandidate.workAuthorization || 'Not Specified'}</div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="p-3 bg-light rounded-3 h-100 d-flex flex-column justify-content-center border">
                                        <div className="small text-muted text-uppercase fw-bold mb-2">Resume / Documents</div>
                                        {selectedCandidate.resume ? (
                                            <div className="d-flex gap-2">
                                                <Button 
                                                    variant="outline-primary" 
                                                    size="sm"
                                                    className="d-inline-flex align-items-center shadow-sm py-2 px-3 fw-bold"
                                                    onClick={() => handleViewResume(
                                                        selectedCandidate.applicationId || selectedCandidate.id, 
                                                        selectedCandidate.resume
                                                    )}
                                                >
                                                    <Eye size={16} className="me-2" />
                                                    View
                                                </Button>
                                                <Button 
                                                    variant="success" 
                                                    size="sm"
                                                    className="d-inline-flex align-items-center shadow-sm py-2 px-3 fw-bold"
                                                    onClick={() => handleDownloadResume(
                                                        selectedCandidate.applicationId || selectedCandidate.id, 
                                                        selectedCandidate.firstName && selectedCandidate.lastName 
                                                            ? `${selectedCandidate.firstName} ${selectedCandidate.lastName}`
                                                            : 'Candidate'
                                                    )}
                                                >
                                                    <FileText size={16} className="me-2" />
                                                    Download
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="text-warning small d-flex align-items-center fw-medium">
                                                <AlertTriangle size={16} className="me-1" />
                                                No resume file uploaded.
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            </Row>

                            {/* Resume Preview Pane */}
                            {(previewLoading || previewUrl) && (
                                <div className="mt-4 p-3 border rounded-3 bg-white shadow-sm">
                                    <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                                        <h6 className="fw-bold mb-0 d-flex align-items-center text-dark">
                                            <FileText size={18} className="me-2 text-primary" />
                                            Resume Preview
                                        </h6>
                                        <Button 
                                            variant="light" 
                                            size="sm" 
                                            onClick={() => {
                                                if (previewUrl && typeof previewUrl === 'string') {
                                                    window.URL.revokeObjectURL(previewUrl);
                                                }
                                                setPreviewUrl(null);
                                                setPreviewType('');
                                            }}
                                            className="p-1 rounded-circle border-0"
                                        >
                                            <X size={16} />
                                        </Button>
                                    </div>
                                    
                                    {previewLoading && (
                                        <div className="text-center py-5">
                                            <Spinner animation="border" variant="primary" className="mb-2" />
                                            <div className="text-muted small">Loading preview...</div>
                                        </div>
                                    )}
                                    
                                    {!previewLoading && previewType === 'word' && (
                                        <Alert variant="warning" className="mb-0 d-flex align-items-start border-0 shadow-sm rounded-3">
                                            <Info size={20} className="me-3 mt-1 flex-shrink-0 text-warning" />
                                            <div>
                                                <div className="fw-bold text-dark">Microsoft Word Document (.doc/.docx)</div>
                                                <div className="small mt-1 text-muted">
                                                    Inline preview is not supported for Word documents. Please click the <strong>Download</strong> button to view this file on your device.
                                                </div>
                                            </div>
                                        </Alert>
                                    )}

                                    {!previewLoading && previewType === 'pdf' && previewUrl && (
                                        <div className="ratio ratio-4x3" style={{ height: '480px' }}>
                                            <iframe 
                                                src={`${previewUrl}#toolbar=0`} 
                                                title="Resume PDF Preview"
                                                className="rounded-3 border shadow-sm w-100 h-100"
                                            />
                                        </div>
                                    )}

                                    {!previewLoading && previewType === 'image' && previewUrl && (
                                        <div className="text-center p-2 border rounded-3 bg-light shadow-sm" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                            <img 
                                                src={previewUrl} 
                                                alt="Candidate Resume Preview" 
                                                className="img-fluid rounded-3" 
                                                style={{ maxWidth: '100%', height: 'auto' }}
                                            />
                                        </div>
                                    )}

                                    {!previewLoading && previewType === 'other' && previewUrl && (
                                        <Alert variant="info" className="mb-0 border-0 shadow-sm rounded-3">
                                            <div className="fw-bold">Preview not available</div>
                                            <div className="small mt-1">
                                                This file format cannot be rendered inline. Please use the <strong>Download</strong> button to view the file.
                                            </div>
                                        </Alert>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseCandidateModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default JobDetailsPage;